import { ReactNode, useState } from 'react';
import { useArgoContext } from '@amzn/awsscm-argo-core';
import Footer from '@amzn/awsscm-argo-core/argo/ui/footer';
import Navigation from '@amzn/awsscm-argo-core/argo/ui/navbar';
import { credentialStorage } from '@amzn/awsscm-argo-core/argo/utils/CredentialStorage';
import { HideableChat } from '@amzn/awsscmui-argo-chatbot-component';
import { Announcements } from 'components/Announcements';
import { Breadcrumbs } from 'components/Breadcrumbs';
import { NavigationMenu } from 'components/side-navigation/Navbar';
import { isExternalArgoUser } from 'rfxportal/RFX/userPermissionHelper';
import { NonCancelableEventHandler } from 'types/uiTypes';

import AppLayout, { AppLayoutProps } from '@amzn/awsui-components-react-v3/polaris/app-layout';
import { BreadcrumbGroupProps } from '@amzn/awsui-components-react-v3/polaris/breadcrumb-group';

import { RFXHeader } from './RFXHeader';

import '@amzn/awsscmui-argo-chatbot-component/argo/index.css';

/**
 * Input for RFXAppLayout.
 *
 * @param breadcrumbsItems Displays a series of navigational links in a hierarchical list.
 * @param content Main content.
 */
export interface RFXAppLayoutProps {
  breadcrumbsItems?: BreadcrumbGroupProps.Item[];
  content: ReactNode;
  splitPanel?: ReactNode;
  splitPanelOpen?: boolean;
  splitPanelPreferences?: AppLayoutProps.SplitPanelPreferences;
  onSplitPanelToggle?: NonCancelableEventHandler<AppLayoutProps.ChangeDetail>;
}

/**
 * Provides the basic layout for all types of pages, including collapsible side navigation, tools panel, and split panel.
 */
export function RFXAppLayout(props: RFXAppLayoutProps) {
  const context = useArgoContext();
  const [navigationOpen, setNavigationOpen] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);

  const [isChatContentVisible, setIsChatContentVisible] = useState(urlParams.has('askArgo'));

  const showChatbot = isChatContentVisible && !isExternalArgoUser();

  const defaultOnSplitPanelToggle: NonCancelableEventHandler = ({ detail }) => {};
  return (
    <div>
      <div id="awsscm-header">
        <RFXHeader
          isChatConentVisible={showChatbot}
          setIsChatContentVisible={(value: boolean) => setIsChatContentVisible(value)}
        />
      </div>
      <div id="awsui">
        <AppLayout
          notifications={<Announcements />}
          breadcrumbs={props.breadcrumbsItems ? <Breadcrumbs items={props.breadcrumbsItems} /> : undefined}
          content={props.content}
          onNavigationChange={() => setNavigationOpen(!navigationOpen)}
          navigationOpen={navigationOpen}
          navigation={<Navigation navigationMenu={<NavigationMenu />} />}
          splitPanel={props.splitPanel}
          splitPanelOpen={props.splitPanelOpen ?? false}
          splitPanelPreferences={props.splitPanelPreferences ?? { position: 'side' }}
          onSplitPanelToggle={props.onSplitPanelToggle ?? defaultOnSplitPanelToggle}
          disableBodyScroll={true}
          headerSelector="#awsscm-header"
          footerSelector="#awsscm-footer"
        />
        <HideableChat
          context={context}
          credentialStorage={credentialStorage}
          isVisible={showChatbot}
          setIsVisible={(visible: boolean) => setIsChatContentVisible(visible)}
        />
      </div>
      <div id="awsscm-footer">
        <Footer />
      </div>
    </div>
  );
}
